import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

/**
 * Calculates responsive column widths based on available screen space
 * Ensures no column deviates more than 20% from its original width
 *
 * Return columns with new calculated width
 */
export const useResponsiveColumnWidths = <T>(
  columnDefs: ColumnDef<T>[],
  screenWidth: number,
): ColumnDef<T>[] => {
  return useMemo(() => {
    const defaultColumWidth = 200;
    // Extract original widths from column definitions
    const totalOriginalWidth = columnDefs.reduce((acc, column) => {
      const originalWidth = column.size || defaultColumWidth; // Default width if not specified

      return acc + originalWidth;
    }, 0);

    // Calculate scaling factor needed to fit screen
    let scalingFactor = screenWidth / totalOriginalWidth;

    // Apply 20% deviation constraint
    const minScalingFactor = 0.8; // 20% reduction max
    const maxScalingFactor = 1.2; // 20% increase max

    // Constrain scaling factor within 20% deviation
    scalingFactor = Math.max(
      minScalingFactor,
      Math.min(maxScalingFactor, scalingFactor),
    );

    return columnDefs.map((column) => ({
      ...column,
      size: Math.round((column.size || defaultColumWidth) * scalingFactor),
    }));
  }, [columnDefs, screenWidth]);
};
